import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'

import { moduleHasReports } from '../reports/reportsConfig'
import { useCanViewGrantsInsightsReports } from '../reports/useCanViewGrantsInsightsReports'
import { useCanViewInsightsReports } from '../reports/useCanViewInsightsReports'
import { useCanViewOrganisations } from '../organisations/useCanViewOrganisations'

import { BreadcrumbsContextController } from './breadcrumbsContext/breadcrumbsContextController/BreadcrumbsContextController'
import { RoutePath, Routes } from './Routes'
import { TopBar } from './topbar/TopBar'
import { NoPrivilegeAccessView } from '@percent/partner-dashboard/common/components'
import { usePermissionCheck } from '@percent/partner-dashboard/common/hooks/useAllow/usePermissionCheck'
import { canViewWGSection } from '@percent/partner-dashboard/context/iam/wgPermissions'
import { Flex, Logo, Navigation, NavItemProps } from '@percent/lemonade'
import { useAuthState } from '@percent/partner-dashboard/common/hooks'
import { selectScopes } from '@percent/partner-dashboard/context/auth'
import { PartnerScopes } from '@percent/partner-dashboard/constants/scopes'
import { NavConfigItem, GetPDNavItemsParams, CreateNavItemParams } from './Layout.types'
import styles from './Layout.module.scss'
import { drawerWidth } from '@percent/partner-dashboard/app/theme/Theme'

// Among sibling sub-items, only the most specific match for the current path is
// active. Otherwise a parent path (/donations) stays highlighted alongside the
// nested one (/donations/reports), since the latter contains the former. Matching
// is segment-aware (exact, or a prefix on a `/` boundary) so /donations never
// matches an unrelated /donationsX route.
export function resolveActiveChildren<T extends { href: string; pathname: string }>(children: T[]) {
  const matchLength = (child: T) => {
    const base = child.href.split('?')[0]

    return child.pathname === base || child.pathname.startsWith(`${base}/`) ? base.length : -1
  }
  const activeLength = Math.max(-1, ...children.map(matchLength))

  return children.map(child => ({ ...child, isActive: activeLength >= 0 && matchLength(child) === activeLength }))
}

function createNavItem({ condition, icon, label, href, children, pathname }: CreateNavItemParams) {
  if (!condition) return null

  return {
    icon,
    label,
    href,
    isActive: pathname.includes(href.includes('?') ? href.split(/\?/)[0] : href.split(/\/+/).find(Boolean) ?? ''),
    ...(children ? { children: resolveActiveChildren(children) } : {})
  }
}

function getPDNavItems({
  pathname,
  scopes,
  userCan,
  canViewInsightsReports,
  canViewGrantsInsightsReports,
  canViewOrganisations,
  t
}: GetPDNavItemsParams) {
  // The insights Reports page lives under Workplace giving once it's available
  // (flag + insights access), surfaced as a submenu item alongside the others.
  const wgHasReports = canViewInsightsReports && moduleHasReports('workplace-giving')
  // Same for the grants Reviews report; the Grants scope its domain needs is already
  // required by the parent nav item's condition.
  const grantsHasReports = canViewGrantsInsightsReports && moduleHasReports('grants')

  const navConfig: NavConfigItem[] = [
    {
      icon: 'menu-dashboard',
      label: t('menu.dashboard'),
      href: '/dashboard',
      condition: true
    },
    {
      icon: 'menu-donations',
      label: t('menu.donations'),
      href: '/donations',
      condition: (scopes[PartnerScopes.Donation] || scopes[PartnerScopes.DonationSession]) && userCan('viewDonations')
    },
    {
      icon: 'menu-categories',
      label: t('menu.categories'),
      href: '/categories',
      condition: scopes[PartnerScopes.Organisation] && userCan('viewCategories')
    },
    {
      icon: 'menu-workplace-giving',
      label: t('menu.workplaceGiving'),
      href: '/workplace-giving/employees',
      condition: scopes[PartnerScopes.WorkplaceGiving] && canViewWGSection(userCan),
      children: [
        {
          label: t('menu.workplaceGivingSubmenu.employees'),
          href: '/workplace-giving/employees'
        },
        ...(scopes[PartnerScopes.DonationLink] && userCan('viewFundraisers')
          ? [
              {
                label: t('menu.workplaceGivingSubmenu.fundraisers'),
                href: '/workplace-giving/fundraisers'
              }
            ]
          : []),
        ...(scopes[PartnerScopes.Volunteering] && userCan('viewOpportunities') && canViewWGSection(userCan)
          ? [
              {
                label: t('menu.workplaceGivingSubmenu.volunteering'),
                href: '/workplace-giving/volunteering'
              }
            ]
          : []),
        ...(scopes[PartnerScopes.GivingCampaign] &&
        scopes[PartnerScopes.WorkplaceGiving] &&
        userCan('viewGivingCampaigns')
          ? [{ label: t('menu.workplaceGivingSubmenu.campaigns'), href: '/workplace-giving/campaigns' }]
          : []),
        ...(scopes[PartnerScopes.Volunteering] && (userCan('viewSurveyTemplates') || userCan('viewWaiverTemplates'))
          ? [{ label: t('menu.workplaceGivingSubmenu.templates'), href: RoutePath.WORKPLACE_GIVING_TEMPLATES }]
          : []),
        ...(scopes[PartnerScopes.Slack]
          ? [{ label: t('menu.workplaceGivingSubmenu.integrations'), href: '/workplace-giving/integrations' }]
          : []),
        {
          label: t('menu.workplaceGivingSubmenu.productRoadmap'),
          href: '/workplace-giving/product-roadmap'
        },
        ...(wgHasReports
          ? [{ label: t('menu.workplaceGivingSubmenu.reports'), href: RoutePath.WORKPLACE_GIVING_REPORTS }]
          : [])
      ]
    },
    {
      icon: 'menu-grants',
      label: t('menu.grants'),
      href: RoutePath.GRANT_TASKS,
      condition: scopes[PartnerScopes.Grants] && userCan('viewGrants'),
      children: [
        {
          label: t('menu.grants.myTasks'),
          href: RoutePath.GRANT_TASKS
        },
        ...(userCan('viewGrantPrograms')
          ? [
              {
                label: t('menu.grants.programs'),
                href: RoutePath.GRANT_PROGRAMS
              }
            ]
          : []),
        ...(grantsHasReports ? [{ label: t('menu.grants.reports'), href: RoutePath.GRANTS_REPORTS }] : [])
      ]
    },
    {
      icon: 'menu-verifications',
      label: t('menu.verifications'),
      href: '/verifications',
      condition:
        (scopes[PartnerScopes.ValidationInvite] || scopes[PartnerScopes.SingleValidationSubmissionApi]) &&
        userCan('viewVerifications')
    },
    {
      icon: 'menu-nonprofits',
      label: t('menu.organisations'),
      href: RoutePath.ORGANISATIONS,
      condition: canViewOrganisations
    }
  ]

  return navConfig
    .map(item =>
      createNavItem({
        ...item,
        pathname,
        children: item.children ? item.children.map(child => ({ ...child, pathname })) : undefined
      })
    )
    .filter((item): item is NavItemProps => !!item)
}

export function Layout() {
  const { t } = useTranslation()
  const { userCan } = usePermissionCheck()
  const { pathname } = useLocation()
  const { partner } = useAuthState()
  const scopes = selectScopes(partner?.scopes)
  const canViewInsightsReports = useCanViewInsightsReports()
  const canViewGrantsInsightsReports = useCanViewGrantsInsightsReports()
  const canViewOrganisations = useCanViewOrganisations()

  if (userCan('viewDashboard')) {
    return (
      <BreadcrumbsContextController>
        <TopBar />
        <Navigation
          logo={<Logo className={styles.logo} />}
          items={getPDNavItems({
            pathname,
            scopes,
            userCan,
            canViewInsightsReports,
            canViewGrantsInsightsReports,
            canViewOrganisations,
            t
          })}
        />
        <Flex direction="column" ml={drawerWidth} p={40}>
          <Routes />
        </Flex>
      </BreadcrumbsContextController>
    )
  }

  return <NoPrivilegeAccessView />
}
